import React, {useEffect, useState} from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  Zoom
} from "@mui/material";
import {IconCheck} from "@tabler/icons-react";
import {green} from "@mui/material/colors";
import {FlowProtectionRepository} from "../../repositories/FlowProtectionRepository";

// Kept in the accessibility/autofill tree but not visible - lets the browser's
// password manager associate the hidden "username" with the password field
// below without showing it in the UI.
const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0
};

/**
 * Prompts for a flow's protection password to open a temporary "unlock"
 * session, letting protected user messages be viewed unmasked. Used from
 * both the dashboard conversation preview and the supervision chat.
 */
export const FlowProtectionAuthModal = ({open, onClose, flowUid, onSuccess}) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setPassword("");
      setError("");
      setSuccess(false);
      setLoading(false);
    }
  }, [open]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!password || loading || !flowUid) return;

    setLoading(true);
    setError("");
    FlowProtectionRepository.authenticate({flowUid, password})
      .then((response) => {
        setLoading(false);
        setSuccess(true);
        const token = response?.data?.token;
        const expiresInSeconds = response?.data?.expiresInSeconds;
        setTimeout(() => {
          onSuccess?.(token, expiresInSeconds);
          onClose?.();
        }, 900);
      })
      .catch((err) => {
        setLoading(false);
        const status = err?.response?.status;
        const message = err?.response?.data?.message || (
          status === 401 ? "Incorrect password." :
            status === 404 ? "No protection password is configured for this flow." :
              "Failed to unlock protected messages."
        );
        setError(message);
      });
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Unlock Protected Messages</DialogTitle>
      <Box component={"form"} onSubmit={handleSubmit}>
        <DialogContent>
          {success ?
            <Stack alignItems={"center"} gap={2} sx={{py: 3}}>
              <Zoom in={success}>
                <Box sx={{
                  bgcolor: green[50],
                  color: green[600],
                  borderRadius: "50%",
                  p: 2,
                  display: "flex"
                }}>
                  <IconCheck size={40}/>
                </Box>
              </Zoom>
              <Typography color={green[700]} fontWeight={500}>Unlocked</Typography>
            </Stack>
            :
            <Stack gap={2} sx={{pt: 1}}>
              <DialogContentText>
                Enter this project's protection password to view protected user messages in plain text.
              </DialogContentText>
              <input
                type={"text"}
                name={"flow-protection-username"}
                autoComplete={"username"}
                value={flowUid || ""}
                readOnly
                style={visuallyHidden}
                tabIndex={-1}
                aria-hidden={"true"}
              />
              <TextField
                autoFocus
                type={"password"}
                label={"Password"}
                name={"flow-protection-unlock-password"}
                autoComplete={"current-password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                error={!!error}
                helperText={error || " "}
                fullWidth
              />
            </Stack>
          }
        </DialogContent>
        {!success &&
          <DialogActions sx={{px: 3, pb: 3}}>
            <Button variant={"ghost"} onClick={onClose} disabled={loading}>Cancel</Button>
            <Button type={"submit"} variant={"primary"} disabled={!password || loading}>
              {loading ? "Checking..." : "Unlock"}
            </Button>
          </DialogActions>
        }
      </Box>
    </Dialog>
  );
};
